<template lang="pug">
  v-container.pb-0(max-height="1000")
    v-snackbar( v-model="snackbar" :color="snackbarColor" ) {{ snackBarMessage }}
      template( v-slot:action="{attrs}")
        v-btn(text v-bind="attrs"
          @click="errorSnackbar = false") {{ $t('buttons.closeButtonLabel') }}
    CalendarEventDialog( v-if="showNewCalendarEvent"
      :showNewCalendarEvent="showNewCalendarEvent"
      @closeDialog="closeDialog($event)")
    ConfirmDialog( v-if="showConfirmDialog"
      :showConfirmDialog="showConfirmDialog"
      :pickedEventStart="pickedEventStart"
      :pickedEventId="pickedEventId"
      @closeConfirmDialog="closeConfirmDialog($event)")
    v-card(flat max-width="1000")
      v-card-title {{ $t('calendarPage.pageTitleLabel') }}
      v-card-text
        v-row.ma-0(no-gutters)
          v-col(cols="8")
            v-toolbar(flat)
              v-btn.newEvent(@click="onNewEventButtonClick"
                color="primary" )
                v-icon( small left ) fas fa-plus
                span {{ $t('calendarPage.newEventButtonLabel') }}
              v-spacer
              v-btn(fab text small
              :disabled="fetchInProgress"
                color="grey darken-2"
                @click="prev")
                v-icon( small) fas fa-chevron-left
              v-progress-circular(v-if="fetchInProgress" indeterminate)
              v-toolbar-title(v-else ) {{ calendarTitle }}
              v-btn(fab text small
              :disabled="fetchInProgress"
                color="grey darken-2"
                @click="next")
                v-icon( small) fas fa-chevron-right
        v-row.ma-0
          v-col(cols="8").px-5.pb-0
            v-sheet(height="600px")
              v-calendar( v-model="calendarValue"
                ref="calendar"
                color="primary"
                show-week
                :weekdays="[1,2,3,4,5]"
                :events="events"
                :event-name="hideName"
                :event-color="getEventColor"
                @click:event="onDeleteButtonClick")
                template(v-slot:event="{event}")
                  v-icon.ma-1.event-icon(color="white").d-flex fal fa-times
          v-col(cols="4")
            v-card( flat ).pl-14
              v-card-title {{ $t('calendarPage.legendTitleLabel') }}
              v-card-text
                v-list-item.pl-0
                  v-list-item-icon
                    v-icon( color="yellow" ) fas fa-circle
                  v-list-item-content
                    v-list-item-title {{ $t('calendarPage.holidayEvent') }}
                v-list-item.pl-0
                  v-list-item-icon
                    v-icon( color="orange" ) fas fa-circle
                  v-list-item-content
                    v-list-item-title {{ $t('calendarPage.freeDayEvent') }}
</template>

<script lang="ts">
import Vue from "vue"
import {Component, Watch} from 'vue-property-decorator'
import CalendarEventDialog from "@/components/calendar/CalendarEventDialog.vue";
import {Action} from "vuex-class";
import {
  FETCH_CALENDAR_EVENTS_FOR_DAY,
} from "@/store/action-types";
import {CalendarEvent} from "vuetify";
import moment from "moment";
import {CalendarEventName} from "@/types/enums";
import ConfirmDialog from "@/components/calendar/ConfirmDialog.vue";
import {FilterEventsPayload, FilterEventsWithCancelTokenPayload} from "@/types/payload";
import CalendarEventForDay from "@/model/calendar/CalendarEventForDay";
import {CancelTokenSource} from "axios";

@Component({
  components: {
    CalendarEventDialog,
    ConfirmDialog
  }
})

export default class CalendarPage extends Vue {
  $refs!: {
    calendar: Vue & {
      prev(): void,
      next(): void,
    }
  }

  @Action(FETCH_CALENDAR_EVENTS_FOR_DAY) fetchCalendarEventsForDay!: { (payload: FilterEventsWithCancelTokenPayload): Promise<CalendarEventForDay[]> }

  private showNewCalendarEvent: boolean = false
  private showConfirmDialog: boolean = false
  private calendarValue: string = moment().format('YYYY-MM-DD')

  private fetchInProgress: boolean = false

  private snackbar: boolean = false
  private snackbarColor: string = ''
  private snackBarMessage: string = ''

  private events: CalendarEvent[] = []
  private pickedEvent: CalendarEvent | null = null
  private pickedEventStart: string = ''
  private pickedEventId: string = ''
  private cancelToken: CancelTokenSource | null = null

  get calendarTitle() {
    return moment(this.calendarValue).locale('sk').format('MMMM YYYY').toLocaleUpperCase()
  }

  @Watch('calendarValue', {immediate: true})
  onCalendarValueChange(newVal: string): void {
    const start = moment(newVal).startOf('month').toDate()
    const end = moment(newVal).endOf('month').toDate()

    this.fetchData(start, end)
  }

  onNewEventButtonClick(): void {
    this.showNewCalendarEvent = true
  }

  onDeleteButtonClick(event: CalendarEvent): void {
    this.pickedEventId = event.event.id
    this.pickedEventStart = event.event.start
    this.showConfirmDialog = true
  }

  closeDialog(wasCreated: boolean): void {
    if (wasCreated) {
      const start = moment(this.calendarValue).startOf('month').toDate()
      const end = moment(this.calendarValue).endOf('month').toDate()

      this.snackbar = true
      this.snackbarColor = 'success'
      this.snackBarMessage = this.$t('success.saveDataSuccess').toString()
      this.fetchData(start, end)
    }

    this.showNewCalendarEvent = false
  }

  closeConfirmDialog(wasDeleted: boolean): void {
    if (wasDeleted) {
      const start = moment(this.calendarValue).startOf('month').toDate()
      const end = moment(this.calendarValue).endOf('month').toDate()

      this.snackbar = true
      this.snackbarColor = 'success'
      this.snackBarMessage = this.$t('success.deleteDataSuccess').toString()
      this.fetchData(start, end)
    }

    this.showConfirmDialog = false
  }

  prev(): void {
    this.$refs.calendar.prev()
  }

  next(): void {
    this.$refs.calendar.next()
  }

  async fetchData(start: Date, end: Date): Promise<void> {

    this.fetchInProgress = true

    this.events = []

    let payloadWithoutCancelToken: FilterEventsPayload = {
      before: end,
      after: start
    }

    let payloadWithCancelToken: FilterEventsWithCancelTokenPayload = {
      payload: payloadWithoutCancelToken,
      cancelToken: this.cancelToken
    }

    try {
      let response: CalendarEventForDay[] = await this.fetchCalendarEventsForDay(payloadWithCancelToken)

      this.events = response.map((e): CalendarEvent => ({
        id: e.id,
        name: e.kind.name,
        start: moment(e.date).format('YYYY-MM-DD'),
      }))
    } catch (e) {
      this.snackbar = true
      this.snackbarColor = 'error'
      this.snackBarMessage = this.$t('errors.fetchDataError').toString()
    } finally {
      this.fetchInProgress = false
    }
  }

  getEventColor(event: CalendarEvent): string {
    switch (event.name) {
      case CalendarEventName.Holiday:
        return "yellow"
      case CalendarEventName.FreeDay:
        return "orange"
      default:
        return ''
    }
  }

  hideName(event: CalendarEvent) {
    switch (event.name) {
      default:
        return ''
    }
  }

  mounted(): void {
    this.calendarValue = moment().format('YYYY-MM-DD')
  }
}
</script>

<style lang="stylus" scoped>

.event-icon {
  font-size: .875em !important;
  justify-content: flex-end;
  margin: 4% 5% 0 0;
}

.newEvent {
  font-size: 17px;
  margin-bottom: 12px;
}

</style>
